import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { SearchInput } from '@provider-portal/common/components/SearchInput';
import { BorderRadius, Shadows } from '@provider-portal/common/styles/themeMui';
import { ModalDialogTitle } from '@provider-portal/menu-common/components/ModalDialogTitle/ModalDialogTitle';

import { EmptySearchResults } from '../EmptySearchResults/EmptySearchResults';

const styles = {
  dialogTitle: { borderBottom: 'solid thin', borderColor: 'colors.neutral300' },
  listSelectedItem: {
    '&.MuiListItem-root': {
      transition: 'none',
      borderRadius: '100px',
      borderStyle: 'solid',
      borderWidth: 'medium',
      margin: '0px',
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
    '&:hover, &:focus': {
      color: 'colors.neutral900',
      backgroundColor: 'colors.neutral300',
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: 'colors.white',
      backgroundColor: 'colors.neutral900',
    },
    '&.Mui-selected:focus': {
      backgroundColor: 'colors.neutral800',
    },
    '.MuiListItemText': {
      margin: 0,
    },
    padding: '2px 14px',
  },
};

export interface GroupedDialogFCProps extends GroupSelectorFCProps {
  open: boolean;
  onClickCancel: () => void;
  onSelectItem: (
    groupId: string,
    groupLabel: string,
    groupItemId: string,
    groupItemLabel: string,
  ) => void;
  selectedProviderId: number;
}

export interface Item {
  id: string;
  label: string;
}

export interface GroupedItems {
  groupId: string;
  groupLabel: string;
  items: Item[];
}

export interface GroupSelectorFCProps {
  strings: Record<
    'mainTitle' | 'searchLabel' | 'noSearchResultsTitle' | 'noSearchResultsSubtitle',
    string
  >;
  currentSelectedGroupId: string;
  currentSelectedGroupItemId: string;
  groupedItemsList: GroupedItems[];
}

interface GroupedListItemsProps {
  groupedItemsList: GroupedItems[];
  currentSelectedGroupId: string;
  currentSelectedItemId: string;
  onItemSelect: (groupId: string, groupLabel: string, itemId: string, itemLabel: string) => void;
}

interface SelectionListItemProps {
  id: string;
  label: string;
  imageUrl?: string;
  selected: boolean;
  shouldShowImages?: boolean;
  onSelect: (id: string, label: string) => void;
}

const SelectionListItem = React.forwardRef<HTMLElement, SelectionListItemProps>(
  ({ id, label, onSelect, selected }, ref) => {
    const onClickListItemHandler = useCallback(() => {
      onSelect(id, label);
    }, [id, label, onSelect]);

    return (
      <ListItem
        onClick={onClickListItemHandler}
        button
        selected={selected}
        sx={styles.listSelectedItem}
      >
        <ListItemText
          id={`list-label-${id}`}
          primary={label}
          ref={ref}
          primaryTypographyProps={{
            noWrap: true,
          }}
        />
      </ListItem>
    );
  },
);

const GroupedItemsEntry = React.forwardRef<HTMLElement, GroupedListItemsProps>(
  (
    { groupedItemsList, currentSelectedItemId, currentSelectedGroupId, onItemSelect },
    onSelectItemRef,
  ) => {
    const onGroupItemHandler = useMemo(() => {
      return groupedItemsList.map((groupedItems, _) => {
        return (itemId: string, itemLabel: string) => {
          onItemSelect(groupedItems.groupId, groupedItems.groupLabel, itemId, itemLabel);
        };
      });
    }, [groupedItemsList, onItemSelect]);

    const generateItemList = useMemo(() => {
      return groupedItemsList.map((groupedItems, groupIdx) => {
        return (
          <Box key={groupedItems.groupLabel}>
            <Box mt={4} mb={2}>
              <Typography variant="h6">{groupedItems.groupLabel}</Typography>
            </Box>

            <Grid
              container
              spacing={{ md: 2 }}
              columnSpacing={{ xs: 0.5, sm: 1, md: 1.5 }}
              rowSpacing={{ xs: 0.3, sm: 0.7, md: 0.7 }}
            >
              {groupedItems.items.map((value) => {
                const isSelected: boolean =
                  groupedItems.groupId === currentSelectedGroupId &&
                  value.id === currentSelectedItemId;

                return (
                  <Grid item xs={12} sm={6} md={4} key={value.id}>
                    <SelectionListItem
                      id={value.id}
                      label={value.label}
                      key={value.id}
                      selected={isSelected}
                      ref={isSelected ? onSelectItemRef : undefined}
                      onSelect={onGroupItemHandler[groupIdx]}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      });
    }, [
      currentSelectedGroupId,
      currentSelectedItemId,
      groupedItemsList,
      onGroupItemHandler,
      onSelectItemRef,
    ]);

    return (
      <Grid>
        <List>{generateItemList}</List>
      </Grid>
    );
  },
);

export const GroupedSelectorDialogFC: React.FC<GroupedDialogFCProps> = memo(
  ({
    open,
    strings,
    selectedProviderId,
    currentSelectedGroupId,
    currentSelectedGroupItemId,
    groupedItemsList,
    onClickCancel,
    onSelectItem,
  }) => {
    const [searchedString, setSearchedString] = useState<string>('');
    const [selectedItemId, setSelectedItemId] = useState<string>(currentSelectedGroupItemId);
    const [selectedGroupId, setSelectedGroupId] = useState<string>(currentSelectedGroupId);
    const selectedItemRef = useRef<HTMLElement>();

    const onChangeRef = useCallback((element) => {
      selectedItemRef.current = element;
      selectedItemRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      });
    }, []);

    const displayableItems: GroupedItems[] = useMemo<GroupedItems[]>(() => {
      if (!searchedString.length) {
        return groupedItemsList ?? [];
      }

      const searchRegexp = new RegExp(`${searchedString}`, 'i');
      const filteredGroups: GroupedItems[] = [];

      for (const group of groupedItemsList) {
        const filteredItems: Item[] = group.items.filter((item) => item.label.match(searchRegexp));
        if (filteredItems.length) {
          filteredGroups.push({
            groupId: group.groupId,
            groupLabel: group.groupLabel,
            items: filteredItems,
          });
        }
      }

      return filteredGroups;
    }, [searchedString, groupedItemsList]);

    useEffect(() => {
      if (
        selectedProviderId !== undefined &&
        selectedProviderId !== null &&
        String(selectedProviderId) !== selectedItemId
      ) {
        setSelectedItemId(String(selectedProviderId));
      }
    }, [selectedItemId, selectedProviderId]);

    const onItemSelectHandler = useCallback(
      (groupId, groupLabel, itemId, itemLabel) => {
        setSelectedGroupId(groupId);
        setSelectedItemId(itemId);
        onSelectItem(groupId, groupLabel, itemId, itemLabel);
      },
      [onSelectItem],
    );

    const showItemList = useMemo(() => {
      if (!displayableItems.length) {
        return <EmptySearchResults strings={strings} />;
      }
      return (
        <GroupedItemsEntry
          groupedItemsList={displayableItems}
          onItemSelect={onItemSelectHandler}
          currentSelectedItemId={selectedItemId}
          currentSelectedGroupId={selectedGroupId}
          ref={onChangeRef}
        />
      );
    }, [
      displayableItems,
      onItemSelectHandler,
      selectedItemId,
      selectedGroupId,
      onChangeRef,
      strings,
    ]);

    const onClickCancelInternalHandler = useCallback(() => {
      onClickCancel();
    }, [onClickCancel]);

    const onSearchStringChange = useCallback((searchStr) => {
      setSearchedString(searchStr);
    }, []);

    return (
      <Box>
        <Dialog
          open={open}
          fullWidth
          scroll="paper"
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            sx: {
              maxWidth: 'md',
              minHeight: '70vh',
              maxHeight: '70vh',
              borderRadius: BorderRadius.StandardDialog,
              boxShadow: Shadows.StandardDialog,
            },
          }}
          onBackdropClick={onClickCancelInternalHandler}
        >
          <DialogTitle id="alert-dialog-title" sx={styles.dialogTitle}>
            <ModalDialogTitle
              cardTitle={strings.mainTitle}
              onClickClose={onClickCancelInternalHandler}
            />
          </DialogTitle>
          <DialogContent>
            <Box mt={2}>
              <SearchInput
                searchLabel={strings.searchLabel}
                defaultValue={searchedString}
                onChange={onSearchStringChange}
              />
            </Box>
            <Box>{showItemList}</Box>
          </DialogContent>
        </Dialog>
      </Box>
    );
  },
);
